function generateLocalId(prefix = "id") {
    return `${prefix}_${Math.random().toString(36).slice(2, 11)}`;
}

function getNestedString(source, parentField, field, fallback = "") {
    if (
        source &&
        source[parentField] &&
        typeof source[parentField][field] === "string"
    ) {
        return source[parentField][field];
    }

    return fallback;
}

function getNestedNumber(source, parentField, field, fallback) {
    if (
        source &&
        source[parentField] &&
        !Number.isNaN(Number(source[parentField][field]))
    ) {
        return Number(source[parentField][field]);
    }

    return fallback;
}

function normalizeImageState(payloadImage, fallbackPreview = "") {
    if (payloadImage && typeof payloadImage === "object") {
        return {
            file: null,
            preview:
                typeof payloadImage.preview === "string"
                    ? payloadImage.preview
                    : fallbackPreview,
            error:
                typeof payloadImage.error === "string"
                    ? payloadImage.error
                    : "",
            removed: false
        };
    }

    if (typeof payloadImage === "string") {
        return {
            file: null,
            preview: payloadImage,
            error: "",
            removed: false
        };
    }

    return {
        file: null,
        preview: fallbackPreview,
        error: "",
        removed: false
    };
}

function getDefaultBadgeItem(payload = {}) {
    return {
        local_id: payload.local_id || generateLocalId("badge_item"),

        content_mode: payload.content_mode || "custom",
        product_variable_key: payload.product_variable_key || "",

        title: {
            ro: getNestedString(payload, "title", "ro", ""),
            en: getNestedString(payload, "title", "en", "")
        },

        title_color: payload.title_color || "#000000",
        image: typeof payload.image === "string" ? payload.image : "",
        image_file: payload.image_file || null,
        image_preview:
            typeof payload.image_preview === "string"
                ? payload.image_preview
                : (typeof payload.image === "string" ? payload.image : ""),
        remove_image: !!payload.remove_image,
        background_color: payload.background_color || "#FFFFFF",
        corner_radius: Number(payload.corner_radius) || 12,
        use_d_shape: !!payload.use_d_shape,

        paddings: {
            top: getNestedNumber(payload, "paddings", "top", 10),
            left: getNestedNumber(payload, "paddings", "left", 10),
            right: getNestedNumber(payload, "paddings", "right", 10),
            bottom: getNestedNumber(payload, "paddings", "bottom", 10)
        },

        margins: {
            top: getNestedNumber(payload, "margins", "top", 0),
            left: getNestedNumber(payload, "margins", "left", 0),
            right: getNestedNumber(payload, "margins", "right", 0),
            bottom: getNestedNumber(payload, "margins", "bottom", 0)
        }
    };
}

function normalizeBadgeList(payloadBadges, positionKey) {
    const positionBadges =
        payloadBadges &&
        payloadBadges[positionKey] &&
        Array.isArray(payloadBadges[positionKey])
            ? payloadBadges[positionKey]
            : [];

    return positionBadges.map((badge) => getDefaultBadgeItem(badge));
}

function normalizeBadges(payload = {}) {
    const badges = payload.badges && typeof payload.badges === "object" ? payload.badges : {};

    return {
        top_left: normalizeBadgeList(badges, "top_left"),
        top_right: normalizeBadgeList(badges, "top_right"),
        bottom_left: normalizeBadgeList(badges, "bottom_left"),
        bottom_right: normalizeBadgeList(badges, "bottom_right")
    };
}

export function getDefaultWidgetCarouselProductItem(payload = {}) {
    return {
        local_id: payload.local_id || generateLocalId("item"),
        voucher_url_use_default:
            typeof payload.voucher_url_use_default === "boolean"
                ? payload.voucher_url_use_default
                : false,
        voucher_url: typeof payload.voucher_url === "string" ? payload.voucher_url : "",
        badges: normalizeBadges(payload)
    };
}

export function getDefaultWidgetCarouselProduct(payload = {}) {
    const isActive =
        typeof payload.is_active === "boolean" ? payload.is_active : true;

    return {
        local_id: payload.local_id || generateLocalId("product"),

        source_type: payload.source_type === "offers" ? "offers" : "benefits",

        product_id: payload.product_id != null ? payload.product_id : null,
        product_name: payload.product_name || "",
        product_name_en: payload.product_name_en || "",
        price: payload.price != null ? String(payload.price) : "",
        discount_price: payload.discount_price != null ? String(payload.discount_price) : "",
        user_discount: payload.user_discount != null ? String(payload.user_discount) : "",
        discount: payload.discount != null ? String(payload.discount) : "",
        value: payload.value != null ? String(payload.value) : "",
        brand_name: payload.brand_name || "",

        priority: payload.priority != null ? Number(payload.priority) : 1,
        is_active: isActive,
        is_locked: !isActive,
        reason: payload.reason || "",

        image: normalizeImageState(payload.image),

        corner_radius: Number(payload.corner_radius) || 12,
        use_d_shape: !!payload.use_d_shape,
        spacing: Number(payload.spacing) || 4,
        background_color: payload.background_color || "",

        margins: {
            top: getNestedNumber(payload, "margins", "top", 8),
            left: getNestedNumber(payload, "margins", "left", 8),
            right: getNestedNumber(payload, "margins", "right", 8),
            bottom: getNestedNumber(payload, "margins", "bottom", 8)
        },

        paddings: {
            top: getNestedNumber(payload, "paddings", "top", 0),
            left: getNestedNumber(payload, "paddings", "left", 0),
            right: getNestedNumber(payload, "paddings", "right", 0),
            bottom: getNestedNumber(payload, "paddings", "bottom", 0)
        },

        items: Array.isArray(payload.items)
            ? payload.items.map((item) => getDefaultWidgetCarouselProductItem(item))
            : []
    };
}

export function normalizeWidgetCarouselForm(payload = {}) {
    return {
        is_active: typeof payload.is_active === "boolean" ? payload.is_active : true,
        is_published: typeof payload.is_published === "boolean" ? payload.is_published : false,
        type: payload.type === "offers" ? "offers" : "benefits",
        widget_id: typeof payload.widget_id === "string" ? payload.widget_id : "",
        tracking: typeof payload.tracking === "string" ? payload.tracking : "",
        priority:
            payload.priority != null &&
            Number(payload.priority) >= 80 &&
            Number(payload.priority) <= 200 &&
            Number(payload.priority) % 5 === 0,
        available_start_date:
            typeof payload.available_start_date === "string" ? payload.available_start_date : "",
        available_end_date:
            typeof payload.available_end_date === "string" ? payload.available_end_date : "",

        ratio: Number(payload.ratio) || 2,
        auto_scroll:
            typeof payload.auto_scroll === "boolean" ? payload.auto_scroll : true,
        display_page_indicator:
            typeof payload.display_page_indicator === "boolean"
                ? payload.display_page_indicator
                : true,

        title: {
            ro: getNestedString(payload, "title", "ro", ""),
            en: getNestedString(payload, "title", "en", "")
        },

        subtitle: {
            ro: getNestedString(payload, "subtitle", "ro", ""),
            en: getNestedString(payload, "subtitle", "en", "")
        },

        footer: {
            ro: getNestedString(payload, "footer", "ro", ""),
            en: getNestedString(payload, "footer", "en", "")
        },

        global_background_image: normalizeImageState(
            payload.global_background_image,
            typeof payload.image === "string" ? payload.image : ""
        ),

        products: Array.isArray(payload.products)
            ? payload.products.map((product) => getDefaultWidgetCarouselProduct(product))
            : []
    };
}